import {
	BufferAttribute,
	Quaternion,
	Vector3
} from 'three';

import { CameraHelperObjects, VisualizableCamera } from './camera-helper.types';

/**
 * Updates camera helper objects to match current camera state
 *
 * Updates:
 * - Cone position and rotation
 * - Target line endpoints
 *
 * @param camera The camera being visualized
 * @param objects The helper objects to update
 * @param visible Whether helpers should be visible
 * @param targetPosition Optional target position for orientation
 */
export const updateCameraObjects = (
	camera: VisualizableCamera,
	objects: CameraHelperObjects,
	visible: boolean = true,
	targetPosition?: Vector3
): void => {
	// Update cone position
	objects.cone.position.copy(camera.position);

	// Update cone rotation to point at target
	if (targetPosition) {
		const direction = new Vector3()
			.subVectors(targetPosition, camera.position)
			.normalize();
		const up = new Vector3(0, -1, 0);
		const quaternion = new Quaternion();
		quaternion.setFromUnitVectors(up, direction);
		objects.cone.setRotationFromQuaternion(quaternion);

		// Update target line
		const linePositions = new Float32Array([
			camera.position.x,
			camera.position.y,
			camera.position.z,
			targetPosition.x,
			targetPosition.y,
			targetPosition.z
		]);
		objects.targetLine.geometry.setAttribute('position', new BufferAttribute(linePositions, 3));
		objects.targetLine.geometry.computeBoundingSphere();
	}

	// Update visibility
	objects.cone.visible = visible;
	objects.targetLine.visible = visible;
};

/**
 * Sets visibility of all camera helper objects
 *
 * @param objects Camera helper objects
 * @param visible Whether objects should be visible
 */
export const setCameraObjectsVisibility = (
	objects: CameraHelperObjects,
	visible: boolean
): void => {
	objects.cone.visible = visible;
	objects.targetLine.visible = visible;
};

